/**
 * Einbau-Finder section
 *
 * Vanilla ES module-style controller for the `installation-finder.liquid`
 * Shopify section. Multiple instances on a page are supported.
 *
 * Responsibilities:
 *  - Lazy-load the Google Maps JavaScript API (Maps + Places + Marker libs).
 *  - Wire Places Autocomplete to the search input — restricted to the
 *    configured countries. Fill hidden inputs with place_id and components.
 *  - Geolocation button: reverse-geocode browser location, populate the same
 *    hidden inputs.
 *  - On submit: POST to `<api_base_url>/find-installation-partners`. Render
 *    anonymized circles + AdvancedMarker pins, fit bounds, remove the blur.
 *  - "Einbau anfragen": show inline request card, scroll into view; second
 *    autocomplete pre-filled with
 *    the search address. POST to `<api_base_url>/submit-installation-request`.
 *
 * Privacy: the response from the find endpoint contains only center
 * coordinates and radius — no IDs, names, or contact info.
 */

(function () {
  "use strict";

  // ===========================================================================
  // Google Maps loader (singleton across the page)
  // ===========================================================================

  let mapsLoaderPromise = null;

  function loadGoogleMaps(apiKey) {
    if (mapsLoaderPromise) return mapsLoaderPromise;

    if (window.google && window.google.maps && window.google.maps.places) {
      mapsLoaderPromise = Promise.resolve(window.google);
      return mapsLoaderPromise;
    }

    mapsLoaderPromise = new Promise((resolve, reject) => {
      const callbackName =
        "__kbfMapsCallback_" + Math.random().toString(36).slice(2);
      window[callbackName] = function () {
        try {
          delete window[callbackName];
        } catch (_) {
          window[callbackName] = undefined;
        }
        if (window.google && window.google.maps) {
          resolve(window.google);
        } else {
          reject(new Error("Google Maps konnte nicht geladen werden."));
        }
      };

      const script = document.createElement("script");
      const params = new URLSearchParams({
        key: apiKey,
        v: "weekly",
        libraries: "places,marker",
        loading: "async",
        callback: callbackName,
      });
      script.src =
        "https://maps.googleapis.com/maps/api/js?" + params.toString();
      script.async = true;
      script.defer = true;
      script.onerror = () =>
        reject(new Error("Google Maps konnte nicht geladen werden."));
      document.head.appendChild(script);
    });

    return mapsLoaderPromise;
  }

  // ===========================================================================
  // Helpers
  // ===========================================================================

  const PHONE_RE = /^\+[1-9]\d{1,14}$/;
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function getComponent(addressComponents, type, useShort) {
    if (!Array.isArray(addressComponents)) return "";
    const c = addressComponents.find(
      (x) => Array.isArray(x.types) && x.types.indexOf(type) !== -1,
    );
    if (!c) return "";
    return useShort ? c.short_name : c.long_name;
  }

  /**
   * Extract the fields we care about from a Google Place result
   * (works for both Autocomplete.getPlace() and Geocoder results).
   */
  function extractPlaceFields(place) {
    const components = place.address_components || [];
    const country = (
      getComponent(components, "country", true) || ""
    ).toUpperCase();
    const postalCode = getComponent(components, "postal_code");
    const city =
      getComponent(components, "locality") ||
      getComponent(components, "postal_town") ||
      getComponent(components, "administrative_area_level_2");

    let lat = null;
    let lng = null;
    if (place.geometry && place.geometry.location) {
      lat =
        typeof place.geometry.location.lat === "function"
          ? place.geometry.location.lat()
          : place.geometry.location.lat;
      lng =
        typeof place.geometry.location.lng === "function"
          ? place.geometry.location.lng()
          : place.geometry.location.lng;
    }

    return {
      placeId: place.place_id || "",
      formattedAddress: place.formatted_address || "",
      country,
      postalCode,
      city,
      lat,
      lng,
    };
  }

  function setHiddenValues(form, fields) {
    if (!form) return;
    const setVal = (selector, value) => {
      const el = form.querySelector(selector);
      if (el) el.value = value == null ? "" : String(value);
    };
    setVal("[data-kbf-place-id]", fields.placeId);
    setVal("[data-kbf-postal-code]", fields.postalCode);
    setVal("[data-kbf-city]", fields.city);
    setVal("[data-kbf-country-code]", fields.country);
    setVal("[data-kbf-lat]", fields.lat);
    setVal("[data-kbf-lng]", fields.lng);
  }

  function clearHiddenValues(form) {
    setHiddenValues(form, {
      placeId: "",
      postalCode: "",
      city: "",
      country: "",
      lat: "",
      lng: "",
    });
  }

  function showHint(hintEl, message, tone) {
    if (!hintEl) return;
    if (!message) {
      hintEl.textContent = "";
      hintEl.hidden = true;
      hintEl.removeAttribute("data-tone");
      return;
    }
    hintEl.hidden = false;
    hintEl.textContent = message;
    if (tone) hintEl.setAttribute("data-tone", tone);
    else hintEl.removeAttribute("data-tone");
  }

  function trim(value) {
    return (value || "").toString().trim();
  }

  // ===========================================================================
  // Per-section controller
  // ===========================================================================

  class InstallationFinder {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.apiBaseUrl = (root.dataset.apiBaseUrl || "").replace(/\/+$/, "");
      this.mapsKey = root.dataset.mapsKey || "";
      this.mapId = root.dataset.mapId || "";
      this.allowedCountries = (root.dataset.allowedCountries || "DE,AT,CH")
        .split(",")
        .map((c) => trim(c).toLowerCase())
        .filter(Boolean);
      this.defaultCenter = {
        lat: parseFloat(root.dataset.defaultLat) || 51.1657,
        lng: parseFloat(root.dataset.defaultLng) || 10.4515,
      };
      this.defaultZoom = parseInt(root.dataset.defaultZoom, 10) || 6;

      this.pinColor = this.parseRgb(root.dataset.pinColor, "13, 64, 198");
      this.radiusFill = this.parseRgb(root.dataset.radiusFill, "13, 64, 198");
      this.radiusStroke = this.parseRgb(
        root.dataset.radiusStroke,
        "13, 64, 198",
      );

      this.searchForm = root.querySelector("[data-kbf-search]");
      this.searchInput = root.querySelector("[data-kbf-address-input]");
      this.geolocateBtn = root.querySelector("[data-kbf-geolocate]");
      this.submitBtn = root.querySelector("[data-kbf-submit]");
      this.hintEl = root.querySelector("[data-kbf-hint]");

      this.mapWrap = root.querySelector("[data-kbf-map-wrap]");
      this.mapEl = root.querySelector("[data-kbf-map]");
      this.mapOverlayEl = root.querySelector("[data-kbf-map-overlay]");

      this.resultEl = root.querySelector("[data-kbf-result]");
      this.resultCountEl = root.querySelector("[data-kbf-count]");
      this.countryWideEl = root.querySelector("[data-kbf-country-wide]");
      this.countryWideCountEl = root.querySelector(
        "[data-kbf-country-wide-count]",
      );
      this.openRequestBtn = root.querySelector("[data-kbf-open-request]");
      this.resultLabelEl = root.querySelector("[data-kbf-result-label]");

      this.dialog = root.querySelector("[data-kbf-dialog]");
      this.dialogForm = root.querySelector("[data-kbf-request-form]");
      this.dialogBanner = root.querySelector("[data-kbf-dialog-banner]");
      this.dialogSubmitBtn = root.querySelector("[data-kbf-dialog-submit]");
      this.dialogSubmitLabel = root.querySelector(
        "[data-kbf-dialog-submit-label]",
      );
      this.dialogSuccessEl = root.querySelector("[data-kbf-dialog-success]");
      this.dialogSuccessRefEl = root.querySelector(
        "[data-kbf-success-reference]",
      );
      this.dialogAddressInput = root.querySelector(
        "[data-kbf-dialog-address-input]",
      );
      this.dialogAddressHint = root.querySelector(
        "[data-kbf-dialog-address-hint]",
      );

      this.map = null;
      this.markers = [];
      this.circles = [];
      this.userMarker = null;
      this.searchAutocomplete = null;
      this.dialogAutocomplete = null;
      this.geocoder = null;

      this.lastSearchFields = null;
      this.dialogFields = null;
      this.lastTotal = 0;

      this._bindEvents();
      this._initMaps();
    }

    parseRgb(value, fallback) {
      const v = trim(value) || fallback;
      const parts = v.split(",").map((s) => parseInt(trim(s), 10));
      if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
        return fallback;
      }
      return parts.join(", ");
    }

    // -----------------------------------------------------------------------
    // Initialisation
    // -----------------------------------------------------------------------

    async _initMaps() {
      if (!this.mapsKey) {
        showHint(
          this.hintEl,
          "Karten-Konfiguration fehlt. Bitte kontaktieren Sie uns telefonisch.",
          "error",
        );
        return;
      }
      if (!this.apiBaseUrl) {
        showHint(this.hintEl, "Such-Endpunkt nicht konfiguriert.", "error");
        return;
      }

      try {
        await loadGoogleMaps(this.mapsKey);
      } catch (err) {
        console.error("[kbf] Failed to load Google Maps:", err);
        showHint(
          this.hintEl,
          "Karte konnte nicht geladen werden. Bitte später erneut versuchen.",
          "error",
        );
        return;
      }

      const google = window.google;
      this.geocoder = new google.maps.Geocoder();

      const mapOptions = {
        center: this.defaultCenter,
        zoom: this.defaultZoom,
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: "cooperative",
        clickableIcons: false,
      };
      if (this.mapId) mapOptions.mapId = this.mapId;

      this.map = new google.maps.Map(this.mapEl, mapOptions);

      this._setupSearchAutocomplete();
      this._setupDialogAutocomplete();
    }

    _setupSearchAutocomplete() {
      const google = window.google;
      if (!this.searchInput) return;

      try {
        this.searchAutocomplete = new google.maps.places.Autocomplete(
          this.searchInput,
          {
            types: ["geocode"],
            componentRestrictions: { country: this.allowedCountries },
            fields: [
              "place_id",
              "geometry",
              "formatted_address",
              "address_components",
            ],
          },
        );
      } catch (err) {
        console.error("[kbf] Places Autocomplete failed to init:", err);
        return;
      }

      this.searchAutocomplete.addListener("place_changed", () => {
        const place = this.searchAutocomplete.getPlace();
        if (!place || !place.place_id) {
          // User pressed enter without picking a suggestion.
          showHint(
            this.hintEl,
            "Bitte wählen Sie einen Vorschlag aus der Liste.",
            "warn",
          );
          this.submitBtn.disabled = true;
          return;
        }
        const fields = extractPlaceFields(place);
        if (!fields.placeId || !fields.lat || !fields.lng) {
          showHint(
            this.hintEl,
            "Adresse konnte nicht ausgelesen werden.",
            "error",
          );
          this.submitBtn.disabled = true;
          return;
        }
        this._applyPlaceToSearch(fields);
      });
    }

    _setupDialogAutocomplete() {
      const google = window.google;
      if (!this.dialogAddressInput) return;

      try {
        this.dialogAutocomplete = new google.maps.places.Autocomplete(
          this.dialogAddressInput,
          {
            types: ["geocode"],
            componentRestrictions: { country: this.allowedCountries },
            fields: [
              "place_id",
              "geometry",
              "formatted_address",
              "address_components",
            ],
          },
        );
      } catch (err) {
        console.error("[kbf] Dialog autocomplete init failed:", err);
        return;
      }

      this.dialogAutocomplete.addListener("place_changed", () => {
        const place = this.dialogAutocomplete.getPlace();
        if (!place || !place.place_id) return;
        const fields = extractPlaceFields(place);
        if (!fields.placeId) return;
        this.dialogFields = fields;
        if (this.dialogAddressHint) {
          this.dialogAddressHint.textContent = "Adresse aktualisiert.";
        }
        this._setError("address", "");
      });
    }

    _applyPlaceToSearch(fields) {
      if (
        !this.allowedCountries.includes((fields.country || "").toLowerCase())
      ) {
        showHint(
          this.hintEl,
          "Wir liefern derzeit nur in die ausgewählten Länder. Bitte wählen Sie eine andere Adresse.",
          "error",
        );
        clearHiddenValues(this.searchForm);
        this.submitBtn.disabled = true;
        return;
      }
      setHiddenValues(this.searchForm, fields);
      this.lastSearchFields = fields;
      this.submitBtn.disabled = false;
      showHint(this.hintEl, "");
    }

    // -----------------------------------------------------------------------
    // Event binding
    // -----------------------------------------------------------------------

    _bindEvents() {
      // Re-typing in the search input invalidates the previously chosen place.
      if (this.searchInput) {
        this.searchInput.addEventListener("input", () => {
          // Keep enabled if a place is still set. The Autocomplete widget
          // doesn't fire input events for selection clicks, so this only
          // triggers on real typing.
          this.submitBtn.disabled = true;
          clearHiddenValues(this.searchForm);
          this.lastSearchFields = null;
        });
        // Pressing Enter without picking a suggestion submits the form
        // prematurely — block it.
        this.searchInput.addEventListener("keydown", (e) => {
          if (e.key === "Enter" && !this.lastSearchFields) {
            e.preventDefault();
          }
        });
      }

      if (this.geolocateBtn) {
        this.geolocateBtn.addEventListener("click", () =>
          this._handleGeolocate(),
        );
      }

      if (this.searchForm) {
        this.searchForm.addEventListener("submit", (e) => {
          e.preventDefault();
          this._handleSearch();
        });
      }

      if (this.openRequestBtn) {
        this.openRequestBtn.addEventListener("click", () => this._openDialog());
      }

      if (this.dialog) {
        const closeBtns = this.dialog.querySelectorAll(
          "[data-kbf-dialog-close], [data-kbf-dialog-cancel]",
        );
        closeBtns.forEach((btn) =>
          btn.addEventListener("click", () => this._closeDialog()),
        );
        const closeSuccessBtn = this.dialog.querySelector(
          "[data-kbf-dialog-close-success]",
        );
        if (closeSuccessBtn) {
          closeSuccessBtn.addEventListener("click", () => this._closeDialog());
        }
      }

      if (this.dialogForm) {
        this.dialogForm.addEventListener("submit", (e) => {
          e.preventDefault();
          this._handleDialogSubmit();
        });
        // Re-typing dialog address invalidates the dialog place.
        if (this.dialogAddressInput) {
          this.dialogAddressInput.addEventListener("input", () => {
            this.dialogFields = null;
            if (this.dialogAddressHint) {
              this.dialogAddressHint.textContent =
                "Bitte einen Vorschlag aus der Liste auswählen.";
            }
          });
          this.dialogAddressInput.addEventListener("keydown", (e) => {
            if (e.key === "Enter") e.preventDefault();
          });
        }
      }

      // Theme editor: Re-init on section reload.
      document.addEventListener("shopify:section:load", (event) => {
        if (event && event.target && event.target.contains(this.root)) {
          this._teardown();
        }
      });
    }

    _teardown() {
      this._clearMapOverlays();
      this.map = null;
    }

    // -----------------------------------------------------------------------
    // Geolocation
    // -----------------------------------------------------------------------

    _handleGeolocate() {
      if (!navigator.geolocation) {
        showHint(
          this.hintEl,
          "Standortabfrage wird vom Browser nicht unterstützt.",
          "error",
        );
        return;
      }
      if (!this.geocoder) {
        showHint(
          this.hintEl,
          "Karte wird noch geladen, bitte einen Moment warten.",
          "warn",
        );
        return;
      }

      showHint(this.hintEl, "Standort wird ermittelt…");
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          this._reverseGeocode(lat, lng);
        },
        (error) => {
          console.warn("[kbf] geolocation error:", error);
          showHint(
            this.hintEl,
            "Standort konnte nicht ermittelt werden. Bitte Adresse manuell eingeben.",
            "error",
          );
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 },
      );
    }

    _reverseGeocode(lat, lng) {
      this.geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status !== "OK" || !results || !results[0]) {
          showHint(
            this.hintEl,
            "Ihr Standort konnte keiner Adresse zugeordnet werden.",
            "error",
          );
          return;
        }
        const place = results[0];
        const fields = extractPlaceFields(place);
        if (!fields.placeId) {
          showHint(
            this.hintEl,
            "Adresse konnte nicht ausgelesen werden.",
            "error",
          );
          return;
        }
        if (this.searchInput) {
          this.searchInput.value = fields.formattedAddress || "";
        }
        this._applyPlaceToSearch(fields);
        // Auto-trigger the search.
        if (!this.submitBtn.disabled) {
          this._handleSearch();
        }
      });
    }

    // -----------------------------------------------------------------------
    // Search
    // -----------------------------------------------------------------------

    async _handleSearch() {
      if (!this.lastSearchFields) {
        showHint(this.hintEl, "Bitte zuerst eine Adresse auswählen.", "warn");
        return;
      }
      const fields = this.lastSearchFields;
      this.submitBtn.disabled = true;
      showHint(this.hintEl, "Suche läuft…");

      try {
        const res = await fetch(
          this.apiBaseUrl + "/find-installation-partners",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              mode: "place_id",
              place_id: fields.placeId,
              country_code: fields.country,
            }),
          },
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          const message =
            data && data.error ? data.error : "Suche fehlgeschlagen.";
          showHint(this.hintEl, message, "error");
          this.submitBtn.disabled = false;
          return;
        }

        this._renderResults(data, fields);
      } catch (err) {
        console.error("[kbf] search error:", err);
        showHint(
          this.hintEl,
          "Verbindung zum Server fehlgeschlagen. Bitte später erneut versuchen.",
          "error",
        );
        this.submitBtn.disabled = false;
      }
    }

    _renderResults(data, fields) {
      const google = window.google;
      this._clearMapOverlays();

      const userLoc = data.userLocation || {};
      const center =
        userLoc.lat && userLoc.lng
          ? { lat: userLoc.lat, lng: userLoc.lng }
          : { lat: fields.lat, lng: fields.lng };

      // Pin user.
      this.userMarker = this._createUserMarker(center);

      // Pin + circle for each anonymized partner.
      const partners = Array.isArray(data.partners) ? data.partners : [];
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(center);

      partners.forEach((p) => {
        const partnerCenter = { lat: p.centerLat, lng: p.centerLng };
        const radiusMeters = (p.radiusKm || 0) * 1000;

        const circle = new google.maps.Circle({
          map: this.map,
          center: partnerCenter,
          radius: radiusMeters,
          strokeColor: "rgb(" + this.radiusStroke + ")",
          strokeOpacity: 0.7,
          strokeWeight: 1.5,
          fillColor: "rgb(" + this.radiusFill + ")",
          fillOpacity: 0.12,
          clickable: false,
        });
        this.circles.push(circle);

        const marker = this._createPartnerMarker(partnerCenter);
        if (marker) this.markers.push(marker);

        const c = circle.getBounds();
        if (c) bounds.union(c);
      });

      if (partners.length > 0) {
        this.map.fitBounds(bounds, 64);
      } else {
        this.map.setCenter(center);
        this.map.setZoom(11);
      }

      // Reveal map.
      this.mapWrap.setAttribute("data-state", "results");

      // Result card (under search).
      const total = data.totalCount || 0;
      this.lastTotal = total;
      this.resultEl.hidden = false;
      this.resultCountEl.textContent = String(data.count || 0);

      if (data.countryWideCount && data.countryWideCount > 0) {
        this.countryWideCountEl.textContent = String(data.countryWideCount);
        this.countryWideEl.hidden = false;
      } else {
        this.countryWideEl.hidden = true;
      }

      if (data.count === 0 && data.countryWideCount === 0) {
        showHint(
          this.hintEl,
          "Aktuell haben wir noch keinen Partner in Ihrer Region — sagen Sie uns trotzdem Bescheid, wir vermitteln gerne.",
          "warn",
        );
        if (this.resultLabelEl) {
          this.resultLabelEl.textContent =
            "Partner in Ihrer Region. Wir können trotzdem helfen.";
        }
      } else {
        showHint(this.hintEl, "");
      }

      this.submitBtn.disabled = false;
    }

    _createUserMarker(position) {
      const google = window.google;
      const useAdvanced =
        google.maps.marker &&
        google.maps.marker.AdvancedMarkerElement &&
        this.mapId;

      if (useAdvanced) {
        const dot = document.createElement("div");
        dot.className = "kbf-user-pin";
        dot.style.cssText =
          "width:14px;height:14px;border-radius:50%;background:#fff;border:3px solid rgb(" +
          this.pinColor +
          ");";
        return new google.maps.marker.AdvancedMarkerElement({
          map: this.map,
          position,
          content: dot,
          title: "Ihr Standort",
        });
      }
      return new google.maps.Marker({
        map: this.map,
        position,
        title: "Ihr Standort",
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: "#ffffff",
          fillOpacity: 1,
          strokeColor: "rgb(" + this.pinColor + ")",
          strokeWeight: 3,
        },
      });
    }

    _createPartnerMarker(position) {
      const google = window.google;
      const useAdvanced =
        google.maps.marker &&
        google.maps.marker.AdvancedMarkerElement &&
        this.mapId;

      if (useAdvanced) {
        const wrap = document.createElement("div");
        wrap.className = "kbf-partner-pin";
        wrap.innerHTML =
          '<svg width="28" height="36" viewBox="0 0 28 36" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
          '<path d="M14 0C6.27 0 0 6.27 0 14c0 9.27 11.55 20.5 13.06 21.95.53.5 1.36.5 1.88 0C16.45 34.5 28 23.27 28 14 28 6.27 21.73 0 14 0Z" fill="rgb(' +
          this.pinColor +
          ')"/>' +
          '<circle cx="14" cy="13" r="5" fill="#ffffff"/>' +
          "</svg>";
        return new google.maps.marker.AdvancedMarkerElement({
          map: this.map,
          position,
          content: wrap,
        });
      }

      return new google.maps.Marker({
        map: this.map,
        position,
        icon: {
          path: "M14 0C6.27 0 0 6.27 0 14c0 9.27 11.55 20.5 13.06 21.95.53.5 1.36.5 1.88 0C16.45 34.5 28 23.27 28 14 28 6.27 21.73 0 14 0Z",
          fillColor: "rgb(" + this.pinColor + ")",
          fillOpacity: 1,
          strokeWeight: 0,
          scale: 1,
          anchor: new google.maps.Point(14, 36),
        },
      });
    }

    _clearMapOverlays() {
      this.markers.forEach((m) => {
        if (m && typeof m.setMap === "function") m.setMap(null);
        else if (m && "map" in m) m.map = null;
      });
      this.circles.forEach((c) => c && c.setMap && c.setMap(null));
      if (this.userMarker) {
        if (typeof this.userMarker.setMap === "function")
          this.userMarker.setMap(null);
        else if ("map" in this.userMarker) this.userMarker.map = null;
      }
      this.markers = [];
      this.circles = [];
      this.userMarker = null;
    }

    // -----------------------------------------------------------------------
    // Request form (inline panel)
    // -----------------------------------------------------------------------

    _openDialog() {
      if (!this.dialog) return;

      this._resetDialog();

      // Pre-fill the dialog address from the search.
      if (this.lastSearchFields && this.dialogAddressInput) {
        this.dialogAddressInput.value =
          this.lastSearchFields.formattedAddress || "";
        this.dialogFields = this.lastSearchFields;
        if (this.dialogAddressHint) {
          this.dialogAddressHint.textContent =
            "Adresse aus der Suche übernommen.";
        }
      }

      this.dialog.hidden = false;

      requestAnimationFrame(() => {
        const reduceMotion =
          typeof window.matchMedia === "function" &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        this.dialog.scrollIntoView({
          behavior: reduceMotion ? "auto" : "smooth",
          block: "start",
        });
        const first = this.dialogForm?.querySelector('input[name="firstname"]');
        if (first && typeof first.focus === "function") first.focus();
      });
    }

    _closeDialog() {
      if (!this.dialog) return;
      this.dialog.hidden = true;
    }

    _resetDialog() {
      if (this.dialogForm) {
        this.dialogForm.hidden = false;
        // Don't reset on every open; only clear errors.
        this._clearAllErrors();
      }
      if (this.dialogSuccessEl) {
        this.dialogSuccessEl.hidden = true;
      }
      if (this.dialogBanner) {
        this.dialogBanner.hidden = true;
        this.dialogBanner.textContent = "";
      }
      if (this.dialogSubmitBtn) {
        this.dialogSubmitBtn.disabled = false;
      }
      if (this.dialogSubmitLabel) {
        this.dialogSubmitLabel.textContent = "Anfrage senden";
      }
    }

    _clearAllErrors() {
      this.root.querySelectorAll("[data-kbf-error]").forEach((el) => {
        el.textContent = "";
      });
    }

    _setError(field, message) {
      const el = this.root.querySelector('[data-kbf-error="' + field + '"]');
      if (el) el.textContent = message || "";
    }

    _validateDialog(formData) {
      this._clearAllErrors();
      let ok = true;
      const requireField = (key, message) => {
        if (!trim(formData.get(key))) {
          this._setError(key, message || "Pflichtfeld");
          ok = false;
        }
      };
      requireField("firstname");
      requireField("lastname");

      const email = trim(formData.get("email"));
      if (!email) {
        this._setError("email", "Pflichtfeld");
        ok = false;
      } else if (!EMAIL_RE.test(email)) {
        this._setError("email", "Bitte gültige E-Mail-Adresse eingeben.");
        ok = false;
      }

      const phone = trim(formData.get("phone"));
      if (!phone) {
        this._setError("phone", "Pflichtfeld");
        ok = false;
      } else if (!PHONE_RE.test(phone)) {
        this._setError(
          "phone",
          "Bitte im Format +491701234567 (E.164) eingeben.",
        );
        ok = false;
      }

      if (!this.dialogFields || !this.dialogFields.placeId) {
        this._setError(
          "address",
          "Bitte Adresse aus der Vorschlagsliste auswählen.",
        );
        ok = false;
      }

      if (!formData.get("consent_privacy")) {
        this._setError(
          "consent_privacy",
          "Bitte stimmen Sie der Datenschutzerklärung zu.",
        );
        ok = false;
      }

      const preferred = trim(formData.get("preferred_appointment_at"));
      if (preferred) {
        // datetime-local has no timezone; assume local TZ.
        const date = new Date(preferred);
        if (Number.isNaN(date.getTime())) {
          this._setError(
            "preferred_appointment_at",
            "Bitte ein gültiges Datum eingeben.",
          );
          ok = false;
        }
      }

      return ok;
    }

    _showDialogBanner(message, tone) {
      if (!this.dialogBanner) return;
      this.dialogBanner.hidden = false;
      this.dialogBanner.textContent = message;
      if (tone) this.dialogBanner.setAttribute("data-tone", tone);
      else this.dialogBanner.removeAttribute("data-tone");
    }

    async _handleDialogSubmit() {
      if (!this.dialogForm) return;
      const formData = new FormData(this.dialogForm);
      if (!this._validateDialog(formData)) return;
      if (!this.dialogFields) return;

      const fields = this.dialogFields;
      const preferred = trim(formData.get("preferred_appointment_at"));
      const isoPreferred = preferred ? new Date(preferred).toISOString() : null;

      const payload = {
        mode: "place_id",
        place_id: fields.placeId,
        country_code: fields.country || "DE",
        firstname: trim(formData.get("firstname")),
        lastname: trim(formData.get("lastname")),
        email: trim(formData.get("email")),
        phone: trim(formData.get("phone")),
        message: trim(formData.get("message")) || null,
        preferred_appointment_at: isoPreferred,
        consent_privacy: true,
      };

      this.dialogSubmitBtn.disabled = true;
      if (this.dialogSubmitLabel)
        this.dialogSubmitLabel.textContent = "Wird gesendet…";
      this._showDialogBanner("", null);
      this.dialogBanner.hidden = true;

      try {
        const res = await fetch(
          this.apiBaseUrl + "/submit-installation-request",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );
        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data.success) {
          if (Array.isArray(data && data.errors)) {
            data.errors.forEach((e) => {
              if (e && e.field) this._setError(e.field, e.message);
            });
            this._showDialogBanner(
              "Bitte überprüfen Sie die markierten Felder.",
              "error",
            );
          } else {
            this._showDialogBanner(
              (data && data.error) ||
                "Anfrage konnte nicht gesendet werden. Bitte später erneut versuchen.",
              "error",
            );
          }
          this.dialogSubmitBtn.disabled = false;
          if (this.dialogSubmitLabel)
            this.dialogSubmitLabel.textContent = "Anfrage senden";
          return;
        }

        // Success.
        if (this.dialogForm) this.dialogForm.hidden = true;
        if (this.dialogSuccessEl) this.dialogSuccessEl.hidden = false;
        if (this.dialogSuccessRefEl && data.referenceId) {
          this.dialogSuccessRefEl.textContent =
            " Ihre Vorgangsnummer: " + data.referenceId + ".";
        }
      } catch (err) {
        console.error("[kbf] dialog submit error:", err);
        this._showDialogBanner(
          "Verbindung zum Server fehlgeschlagen. Bitte später erneut versuchen.",
          "error",
        );
        this.dialogSubmitBtn.disabled = false;
        if (this.dialogSubmitLabel)
          this.dialogSubmitLabel.textContent = "Anfrage senden";
      }
    }
  }

  // ===========================================================================
  // Bootstrap (handle multiple sections + theme editor reloads)
  // ===========================================================================

  function initAll(scope) {
    const root = scope || document;
    const finders = root.querySelectorAll("[data-kbf]");
    finders.forEach((el) => {
      if (el.dataset.kbfInited === "1") return;
      el.dataset.kbfInited = "1";
      // eslint-disable-next-line no-new
      new InstallationFinder(el);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (event && event.target) initAll(event.target);
  });
})();
